import React from 'react';
import {Button, DatePicker, Input, Space} from 'antd';
import {useTranslation} from 'react-i18next';
import dayjs, {Dayjs} from 'dayjs';
import {ReviewBizType, ReviewStatus} from '../graphql/types/ReviewTaskType';
import {
    useFilterContainerClassName,
    useFilterInputContainerClassName,
    useFilterLabelClassName,
} from '../../../../utils/languageUtils';
import {getSmartLabelClassName} from '../../../../utils/labelWidthUtils';
import {useDspContactOptions} from '../graphql/queries/getDspContactOptions';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import BatchInputFilterItem from '../../../delivery/components/BatchInputFilterItem';
import {
    useReviewBizTypes,
    useReviewBizSubTypes,
} from '../graphql/queries/getReviewBizTypes';

const {RangePicker} = DatePicker;

export interface ReviewTaskFilters {
    reviewTaskId?: string;
    /**
     * 运单号原始字符串；支持多个运单号以分号/逗号/换行分隔
     * 拆分由后端 audit-service 完成（与 PoD/Parcel 既有页面对齐）
     */
    trackingNumbers?: string;
    /** 选中 DSP 后的 ID，仅 number，避免与字符串关键字混用 */
    dspId?: number;
    /** 审核状态，多选；undefined / [] 表示不限 */
    statuses?: string[];
    /** 一级审核类型（review_task.biz_type，如 CUSTOMER_COMPLAINT） */
    bizType?: string;
    /** 二级业务分类（按 bizType 路由到对应业务模块的细分枚举，如 complaint_type） */
    bizSubType?: string;
    submittedAt?: [Dayjs, Dayjs];
    /** 审核完成时间区间；仅作用于已审核工单 */
    reviewedAt?: [Dayjs, Dayjs];
}

interface ReviewTaskFilterProps {
    filters: ReviewTaskFilters;
    onChange: (filters: ReviewTaskFilters) => void;
    onSearch: () => void;
    onReset: () => void;
    /** 按钮区右侧的额外操作（如批量审核），可选 */
    extraActions?: React.ReactNode;
}

const ReviewTaskFilter: React.FC<ReviewTaskFilterProps> = ({
    filters,
    onChange,
    onSearch,
    onReset,
    extraActions,
}) => {
    const {t} = useTranslation();
    const {dsps, loading: dspLoading} = useDspContactOptions();
    const {options: bizTypeOptions, loading: bizTypeLoading} = useReviewBizTypes();
    const {options: bizSubTypeOptions, loading: bizSubTypeLoading} = useReviewBizSubTypes(
        filters.bizType
    );

    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();

    // 周期上限 1 个月：用户已选 startDate 时，禁选与 startDate 间隔超过 1 月的日期
    // 双向限制（选 start 后限 end；选 end 后限 start），由 antd RangePicker info.from 提供
    const buildOneMonthDisabledDate = () => (current: Dayjs, info?: {from?: Dayjs}) => {
        if (!current || !info?.from) return false;
        return Math.abs(current.diff(info.from, 'day')) > 31;
    };

    const dspOptions = dsps.map((dsp) => ({
        value: Number(dsp.id),
        // label 同时包含 name 与 contactPhone，ParcelFilterStyle 的 label 模糊匹配可直接命中两者
        label: `${dsp.name}${dsp.contactPhone ? ` (${dsp.contactPhone})` : ''}`,
    }));

    // 列标签集合：用于按列对齐 label 宽度
    const labelReviewTaskId = String(t('reviewTask.filter.reviewTaskId'));
    const labelTrackingNumber = String(t('reviewTask.filter.trackingNumber'));
    const labelDspKeyword = String(t('reviewTask.filter.dspKeyword'));
    const labelStatus = String(t('reviewTask.filter.status'));
    const labelBizType = String(t('reviewTask.filter.bizType'));
    const labelBizSubType = String(t('reviewTask.filter.bizSubType'));
    const labelSubmittedAt = String(t('reviewTask.filter.submittedAt'));
    const labelReviewedAt = String(t('reviewTask.filter.reviewedAt'));

    // 运单号独占一行（参考 PoD 模式）；下面 3 列布局对齐的 label 集合
    // 3 行 3 列：
    //   行1 [任务ID]    [DSP]       [工单状态]
    //   行2 [审核类型]  [业务类型]   [申诉时间范围]
    //   行3 [审核时间范围] （空）    （空）
    const column1Labels = [labelReviewTaskId, labelBizType, labelReviewedAt];
    const column2Labels = [labelDspKeyword, labelBizSubType];
    const column3Labels = [labelStatus, labelSubmittedAt];

    const labelCls1 = useFilterLabelClassName(getSmartLabelClassName(column1Labels));
    const labelCls2 = useFilterLabelClassName(getSmartLabelClassName(column2Labels));
    const labelCls3 = useFilterLabelClassName(getSmartLabelClassName(column3Labels));

    // 运单号 label 与下方第一列对齐
    const labelClsTracking = getSmartLabelClassName(column1Labels);

    // 「全部」由 ParcelFilterStyle 通过 placeholder + allowClear 表达，options 不再额外加空项
    const statusOptions = [
        {value: ReviewStatus.PENDING_REVIEW, label: t('reviewTask.status.PENDING_REVIEW')},
        {value: ReviewStatus.APPROVED, label: t('reviewTask.status.APPROVED')},
        {value: ReviewStatus.REJECTED, label: t('reviewTask.status.REJECTED')},
        {value: ReviewStatus.CANCELLED, label: t('reviewTask.status.CANCELLED')},
    ];

    // 一级「审核类型」下拉项（由后端 getReviewBizTypeList 提供）。
    //
    // 后端返回 ReviewBizType 全部枚举值（含本页不支持的业务，如 POP_REVIEW），
    // 故按前端 ReviewBizType 白名单过滤 —— 本页只服务客诉：bizType 在 Page 层写死
    // CUSTOMER_COMPLAINT，未过滤的类型即使被选中也查不到数据，且本地 i18n 无其文案会显示原始码值。
    // 本页接入新业务时，在前端 ReviewBizType 枚举补对应项即可。
    // 用 Object.values 而非 `code in ReviewBizType`：后者对 toString 等继承属性也返回 true
    const supportedBizTypes: string[] = Object.values(ReviewBizType);
    const bizTypeSelectOptions = bizTypeOptions
        .filter((opt) => supportedBizTypes.includes(opt.code))
        .map((opt) => ({
            value: opt.code,
            label: t(`reviewTask.bizType.${opt.code}`),
        }));

    // 二级「业务类型」下拉项（按当前选中的 bizType 路由查询）
    // 一期 bizType=CUSTOMER_COMPLAINT → 客诉 complaint_type 枚举
    const bizSubTypeSelectOptions = bizSubTypeOptions.map((opt) => ({
        value: opt.code,
        // 复用 customerComplaint.type i18n（与 DSP 端工作台共用）
        label: t(`customerComplaint.type.${opt.code}`, opt.code),
    }));

    return (
        <div className="mb-2 py-2">
            {/* 运单号独占一行（参考 PoD/Parcel 模式） */}
            <div className="mb-4">
                <BatchInputFilterItem
                    label={labelTrackingNumber}
                    value={filters.trackingNumbers}
                    placeholder={t('reviewTask.filter.trackingNumberPlaceholder') as string}
                    onChange={(e) =>
                        onChange({
                            ...filters,
                            trackingNumbers: e.target.value || undefined,
                        })
                    }
                    rows={2}
                    spanClassName={labelClsTracking}
                />
            </div>
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls1}>{labelReviewTaskId}</span>
                        <div className={inputContainerClassName}>
                            <Input
                                size="middle"
                                className="w-full"
                                value={filters.reviewTaskId}
                                placeholder={t('reviewTask.filter.reviewTaskIdPlaceholder') as string}
                                onChange={(e) => onChange({...filters, reviewTaskId: e.target.value})}
                                allowClear
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelDspKeyword}
                        options={dspOptions}
                        // ParcelFilterStyle 内部把 value 强转字符串，这里同步转回，保证回显
                        value={filters.dspId != null ? String(filters.dspId) : undefined}
                        onChange={(value) =>
                            onChange({
                                ...filters,
                                dspId: value ? Number(value) : undefined,
                            })
                        }
                        loading={dspLoading}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelStatus}
                        options={statusOptions}
                        mode="multiple"
                        maxTagCount="responsive"
                        value={filters.statuses ?? []}
                        onChange={(value) => {
                            // multi-select 时 value 是 string[]；空数组等价于"全部"
                            const arr = Array.isArray(value) ? value : [];
                            onChange({...filters, statuses: arr.length ? arr : undefined});
                        }}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelBizType}
                        options={bizTypeSelectOptions}
                        value={filters.bizType || undefined}
                        onChange={(value) => {
                            // 必填字段：忽略清空动作（value 为空时不更新）
                            if (!value) return;
                            // 切换一级时清空二级，避免「上一个一级选中的二级值」残留
                            onChange({...filters, bizType: value, bizSubType: undefined});
                        }}
                        loading={bizTypeLoading}
                        allowClear={false}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelBizSubType}
                        options={bizSubTypeSelectOptions}
                        value={filters.bizSubType || undefined}
                        onChange={(value) =>
                            onChange({...filters, bizSubType: value || undefined})
                        }
                        loading={bizSubTypeLoading}
                        disabled={!filters.bizType}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                {/* 行 2 col3：申诉时间范围（必填，不可清空） */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls3}>{labelSubmittedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                allowClear={false}
                                value={filters.submittedAt ?? null}
                                disabledDate={buildOneMonthDisabledDate()}
                                onChange={(range) => {
                                    // 必填字段：忽略清空动作，仅在区间完整时才更新；
                                    // 当天 [00:00, 23:59:59.999] 的规整统一在转入参处（ReviewTaskPage）做
                                    if (range && range[0] && range[1]) {
                                        onChange({...filters, submittedAt: [range[0], range[1]]});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                {/* 行 3 col1：审核时间范围 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls1}>{labelReviewedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                value={filters.reviewedAt ?? null}
                                disabledDate={buildOneMonthDisabledDate()}
                                onChange={(range) => {
                                    if (range && range[0] && range[1]) {
                                        onChange({...filters, reviewedAt: [range[0], range[1]]});
                                    } else {
                                        onChange({...filters, reviewedAt: undefined});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-end items-center mt-3">
                <Space>
                    {extraActions}
                    <Button type="primary" onClick={onSearch} className="w-auto min-w-24">
                        <span className="font-bold">{t('reviewTask.filter.search')}</span>
                    </Button>
                    <Button onClick={onReset} className="w-auto min-w-24">
                        <span className="font-bold">{t('reviewTask.filter.reset')}</span>
                    </Button>
                </Space>
            </div>
        </div>
    );
};

export const getDefaultReviewTaskFilters = (): ReviewTaskFilters => ({
    // bizType 必填，默认锁定为一期唯一选项 CUSTOMER_COMPLAINT
    bizType: ReviewBizType.CUSTOMER_COMPLAINT,
    // 申诉时间默认最近一个月：起始日 00:00:00.000，终止日 23:59:59.999
    submittedAt: [
        dayjs().subtract(1, 'month').startOf('day'),
        dayjs().endOf('day'),
    ],
});

export default ReviewTaskFilter;
